import { Router, Routes } from '@angular/router';
import { inject } from '@angular/core';
import { Routing } from '@enums/routing.enum';
import { authGuard, noAuthGuard, adminGuard, moduleGuard } from '@services/auth';

export const routes: Routes = [
	// Protected routes - require authentication (LAZY LOADED)
	{
		// Zakładka „Menu” (mobile): wszystkie moduły jako siatka ikon + ustawienia aplikacji.
		path: Routing.PanelMenu,
		loadComponent: () =>
			import('./modules/cms/mobile-menu/mobile-menu.component').then(
				(m) => m.MobileMenuComponent,
			),
		canActivate: [authGuard],
	},
	{
		path: Routing.PanelDashboard,
		loadComponent: () =>
			import('./modules/cms/dashboard/dashboard.component').then((m) => m.DashboardComponent),
		// moduleGuard gates demo accounts here too (legacy/admin pass — Dashboard has no module).
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelCurrencyList,
		loadComponent: () =>
			import('./modules/cms/currency/currency-list/currency-list.component').then(
				(m) => m.CurrencyListComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelCurrencyDetails + '/:symbol',
		loadComponent: () =>
			import('./modules/cms/currency/currency-details/currency-details.component').then(
				(m) => m.CurrencyDetailsComponent,
			),
		canActivate: [authGuard],
	},
	{
		path: Routing.PanelCurrencyAnalytics + '/:symbol',
		loadComponent: () =>
			import('./modules/cms/currency/currency-analytics/currency-analytics.component').then(
				(m) => m.CurrencyAnalyticsComponent,
			),
		canActivate: [authGuard],
	},
	{
		path: Routing.PanelAccountSettings,
		loadComponent: () =>
			import('./modules/cms/account/account-settings/account-settings.component').then(
				(m) => m.AccountSettingsComponent,
			),
		canActivate: [authGuard],
	},
	{
		path: Routing.PanelSecuritySettings,
		loadComponent: () =>
			import('./modules/cms/account/security-settings/security-settings.component').then(
				(m) => m.SecuritySettingsComponent,
			),
		canActivate: [authGuard],
	},
	{
		path: Routing.PanelTwoFactorSettings,
		loadComponent: () =>
			import('./modules/cms/account/two-factor-settings/two-factor-settings.component').then(
				(m) => m.TwoFactorSettingsComponent,
			),
		canActivate: [authGuard],
	},
	{
		path: Routing.PanelGdprSettings,
		loadComponent: () =>
			import('./modules/cms/account/gdpr-settings/gdpr-settings.component').then(
				(m) => m.GdprSettingsComponent,
			),
		canActivate: [authGuard],
	},
	{
		path: Routing.PanelSentiment,
		loadComponent: () =>
			import(
				'./modules/cms/sentiment/sentiment-dashboard/sentiment-dashboard.component'
			).then((m) => m.SentimentDashboardComponent),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelNotifications,
		loadComponent: () =>
			import('./modules/cms/notifications/notifications.component').then(
				(m) => m.NotificationsComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelVolume,
		loadComponent: () =>
			import('./modules/cms/volume/volume-dashboard.component').then(
				(m) => m.VolumeDashboardComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelLiquidity,
		loadComponent: () =>
			import('./modules/cms/liquidity/liquidity-map.component').then(
				(m) => m.LiquidityMapComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelTrend,
		loadComponent: () =>
			import('./modules/cms/trend/trend-from-low.component').then(
				(m) => m.TrendFromLowComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelMarketBottoms,
		loadComponent: () =>
			import('./modules/cms/market-bottoms/market-bottoms.component').then(
				(m) => m.MarketBottomsComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		// Diagnostic trend-line visualization (shadow harness) — ungated (authGuard only).
		path: Routing.PanelTrendLines,
		loadComponent: () =>
			import('./modules/cms/trend-lines/trend-lines.component').then(
				(m) => m.TrendLinesComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		// Doradca alertów — podpowiedzi dla każdego.
		path: Routing.PanelAdvisor,
		loadComponent: () =>
			import('./modules/cms/alert-advisor/alert-advisor.component').then(
				(m) => m.AlertAdvisorComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelMyTrades,
		loadComponent: () =>
			import('./modules/cms/my-trades/my-trades.component').then((m) => m.MyTradesComponent),
		canActivate: [authGuard, moduleGuard],
	},
	{
		// Auto Doradca — wspólny portfel próbny strategii, sam podejmuje decyzje (tylko admin).
		path: Routing.PanelAutoAdvisor,
		loadComponent: () =>
			import('./modules/cms/advisor/advisor.component').then((m) => m.AdvisorComponent),
		canActivate: [authGuard, adminGuard],
	},
	{
		path: Routing.PanelVolumetric,
		loadComponent: () =>
			import('./modules/cms/volumetric/volumetric.component').then(
				(m) => m.VolumetricComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelMonitor,
		loadComponent: () =>
			import('./modules/cms/trend-monitor/trend-monitor.component').then(
				(m) => m.TrendMonitorComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelWatchlist,
		loadComponent: () =>
			import('./modules/cms/investing/watchlist/investing-watchlist.component').then(
				(m) => m.InvestingWatchlistComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelPortfolio,
		loadComponent: () =>
			import(
				'./modules/cms/portfolio/portfolio-dashboard/portfolio-dashboard.component'
			).then((m) => m.PortfolioDashboardComponent),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelKlimat,
		loadComponent: () =>
			import('./modules/cms/market-climate/market-climate.component').then(
				(m) => m.MarketClimateComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelConsolidations,
		loadComponent: () =>
			import('./modules/cms/consolidations/consolidations.component').then(
				(m) => m.ConsolidationsComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		// Social Media (tylko admin): posty, rolki, relacje i grafiki na FB/IG/X/LinkedIn/TikTok.
		path: Routing.PanelSocial,
		loadComponent: () =>
			import('./modules/cms/social/social.component').then((m) => m.SocialComponent),
		canActivate: [authGuard, adminGuard],
	},
	{
		// Powrót z logowania OAuth platformy — kod oddajemy do API w zalogowanym żądaniu.
		path: Routing.PanelSocialCallback + '/:provider',
		loadComponent: () =>
			import('./modules/cms/social/social-callback/social-callback.component').then(
				(m) => m.SocialCallbackComponent,
			),
		canActivate: [authGuard, adminGuard],
	},
	{
		// Quant (tylko admin): rejestr przewag, werdykty hipotez, stan danych — to opis METODY,
		// więc nigdy dla użytkownika (docs/quant/ROLLOUT.md, etap 9).
		path: Routing.PanelQuant,
		loadComponent: () =>
			import('./modules/cms/quant/quant.component').then((m) => m.QuantComponent),
		canActivate: [authGuard, adminGuard],
	},
	{
		// Admin-only sales presentations (full-screen decks). First deck: Klimat rynku.
		path: Routing.PanelPresentations,
		loadComponent: () =>
			import('./modules/cms/presentations/presentations.component').then(
				(m) => m.PresentationsComponent,
			),
		canActivate: [authGuard, adminGuard],
	},
	{
		// Quiz for a module the user fully owns → unlocks a preview of the next module.
		path: Routing.PanelQuiz + '/:module',
		loadComponent: () =>
			import('./modules/cms/quiz/quiz.component').then((m) => m.QuizComponent),
		canActivate: [authGuard],
	},
	{
		// „Radar trendu” — reżim, BTC (systemy, stopy), radar wybić, konsolidacje, struktura.
		path: Routing.PanelCatchTrend,
		loadComponent: () =>
			import('./modules/cms/catch-trend/catch-trend.component').then(
				(m) => m.CatchTrendComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		// "Stan trendu" jest teraz widokiem Radaru trendu — stary adres prowadzi do niego.
		path: Routing.PanelTrendState,
		redirectTo: () =>
			inject(Router).createUrlTree(['/', Routing.PanelCatchTrend], {
				queryParams: { widok: 'rynek' },
			}),
	},
	{
		// "Monitor wzrostow i wyczerpania" — multi-TF growth radar with regime/location context.
		path: Routing.PanelGainsMonitor,
		loadComponent: () =>
			import('./modules/cms/gains-monitor/gains-monitor.component').then(
				(m) => m.GainsMonitorComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		// „Zakres dnia” — dzienny zakres względem doby UTC (docs/DAILY-RANGE-MODULE.md).
		path: Routing.PanelDailyRange,
		loadComponent: () =>
			import('./modules/cms/daily-range/daily-range.component').then(
				(m) => m.DailyRangeComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		// „Overbalance” (Gann) — korekta trendu na tle wcześniejszych (docs/OVERBALANCE-MODULE.md).
		path: Routing.PanelOverbalance,
		loadComponent: () =>
			import('./modules/cms/overbalance/overbalance.component').then(
				(m) => m.OverbalanceComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		// "Strategia EMA200" jest teraz widokiem Radaru trendu — stary adres prowadzi do niego.
		path: Routing.PanelEmaStrategy,
		redirectTo: () =>
			inject(Router).createUrlTree(['/', Routing.PanelCatchTrend], {
				queryParams: { widok: 'strategie' },
			}),
	},
	{
		// Teaser for a quiz-unlocked module (funnel option A). No moduleGuard — always reachable.
		path: Routing.PanelPreview + '/:module',
		loadComponent: () =>
			import('./modules/cms/module-preview/module-preview.component').then(
				(m) => m.ModulePreviewComponent,
			),
		canActivate: [authGuard],
	},
	{
		path: Routing.PanelExchangeAccounts,
		loadComponent: () =>
			import('./modules/cms/exchange-accounts/exchange-accounts.component').then(
				(m) => m.ExchangeAccountsComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelBtcAnalytics,
		loadComponent: () =>
			import('./modules/cms/btc-analytics/btc-analytics-page.component').then(
				(m) => m.BtcAnalyticsPageComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelOil,
		loadComponent: () => import('./modules/cms/oil/oil.component').then((m) => m.OilComponent),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelSrZones,
		loadComponent: () =>
			import('./modules/cms/sr-zones/sr-zones-list/sr-zones-list.component').then(
				(m) => m.SrZonesListComponent,
			),
		canActivate: [authGuard, moduleGuard],
	},
	{
		path: Routing.PanelAnalytics360,
		loadComponent: () =>
			import('./modules/cms/analytics-360/analytics-360.component').then(
				(m) => m.Analytics360Component,
			),
		canActivate: [authGuard, moduleGuard],
	},
	// Upsell screen — where moduleGuard redirects when a tier lacks the module.
	// Only authGuard here (NO moduleGuard, or it would loop).
	{
		path: Routing.PanelUpgrade,
		loadComponent: () =>
			import('./modules/cms/upgrade/upgrade.component').then((m) => m.UpgradeComponent),
		canActivate: [authGuard],
	},
	// Public auth routes - redirect to dashboard if already authenticated (LAZY LOADED)
	{
		path: Routing.PanelLogin,
		loadComponent: () =>
			import('./modules/cms/auth/login/login.component').then((m) => m.LoginComponent),
		canActivate: [noAuthGuard],
	},
	// Self-service registration (demo accounts): sign up → demo Klimat (blurred) → buy to unlock.
	{
		path: Routing.PanelRegister,
		loadComponent: () =>
			import('./modules/cms/auth/register/register.component').then(
				(m) => m.RegisterComponent,
			),
		canActivate: [noAuthGuard],
	},
	// Public sales / offer page — bespoke individual deployment pitch (no auth guard)
	{
		path: Routing.Offer,
		loadComponent: () =>
			import('./modules/static-pages/offer/offer.component').then((m) => m.OfferComponent),
	},
	// Public Privacy Policy (no auth guard) — required public URL for app stores
	{
		path: Routing.Privacy,
		loadComponent: () =>
			import('./modules/static-pages/privacy/privacy.component').then(
				(m) => m.PrivacyComponent,
			),
	},
	// Public Account & Data Deletion page (no auth guard) — required public URL for Google Play
	{
		path: Routing.AccountDeletion,
		loadComponent: () =>
			import('./modules/static-pages/account-deletion/account-deletion.component').then(
				(m) => m.AccountDeletionComponent,
			),
	},
	{
		path: Routing.PanelForgotPassword,
		loadComponent: () =>
			import('./modules/cms/auth/forgot-password/forgot-password.component').then(
				(m) => m.ForgotPasswordComponent,
			),
		canActivate: [noAuthGuard],
	},
	{
		path: Routing.PanelForgotPassword + '/:token',
		loadComponent: () =>
			import('./modules/cms/auth/forgot-password/forgot-password.component').then(
				(m) => m.ForgotPasswordComponent,
			),
	},
	{
		path: Routing.PanelVerifyEmail,
		loadComponent: () =>
			import('./modules/cms/auth/verify-email/verify-email.component').then(
				(m) => m.VerifyEmailComponent,
			),
	},
	// Film instruktażowy alertu (pełny ekran, bez powłoki) — nagrywany do MP4.
	{
		path: 'alert-film/:id',
		loadComponent: () =>
			import('./modules/alert-films/alert-film.component').then((m) => m.AlertFilmComponent),
		canActivate: [authGuard],
	},
	// Admin routes - require admin role (LAZY LOADED)
	{
		path: Routing.PanelAdmin,
		loadChildren: () => import('./modules/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
		canActivate: [authGuard, adminGuard],
	},
	// Legacy Polish slugs → English (keep old bookmarks / shared links working; no 404).
	{ path: 'klimat-rynku', redirectTo: 'market-climate', pathMatch: 'full' },
	{ path: 'prezentacje', redirectTo: 'presentations', pathMatch: 'full' },
	{ path: 'stan-trendu', redirectTo: 'trend-state', pathMatch: 'full' }, // → Radar trendu, widok Rynek 200d
	{ path: 'monitor-wzrostow', redirectTo: 'gains-monitor', pathMatch: 'full' },
	{ path: 'strategie-trendowe', redirectTo: 'trend-strategies', pathMatch: 'full' }, // → Radar trendu, widok Strategie
	{ path: 'analityka-360', redirectTo: 'analytics-360', pathMatch: 'full' },

	// Public home route - EAGER (initial page)
	{
		path: '',
		loadComponent: () => import('./modules/cms/home.component').then((m) => m.HomeComponent),
	},
	// 404 - must be last - LAZY
	{
		path: '**',
		loadComponent: () =>
			import('./modules/static-pages/err404/err404.component').then((m) => m.Err404Component),
	},
];
